const SCOPED_BULK_OPERATION_LINK_PREFIX = 'grid:';
// IDs are opaque API identifiers, never paths, dot segments, query strings, or fragments.
const BULK_OPERATION_ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9:_-]*$/;

/** URL-persisted reference to an operation, scoped to its owning grid instance when known. */
export type BulkOperationLink = {
  bulkOperationId: string;
  gridCode: string | null;
  presetInstanceId: string | null;
};

export function serializeBulkOperationLink(
  gridCode: string,
  presetInstanceId: string | undefined,
  bulkOperationId: string
): string {
  return `${SCOPED_BULK_OPERATION_LINK_PREFIX}${encodeURIComponent(gridCode)}:${encodeURIComponent(
    presetInstanceId ?? ''
  )}:${encodeURIComponent(bulkOperationId)}`;
}

export function parseBulkOperationLink(value: string | null): BulkOperationLink | null {
  if (value === null || value.length === 0) {
    return null;
  }

  if (!value.startsWith(SCOPED_BULK_OPERATION_LINK_PREFIX)) {
    return BULK_OPERATION_ID_PATTERN.test(value)
      ? {bulkOperationId: value, gridCode: null, presetInstanceId: null}
      : null;
  }

  const scopedValue = value.slice(SCOPED_BULK_OPERATION_LINK_PREFIX.length);
  const parts = scopedValue.split(':');
  if (
    (parts.length !== 2 && parts.length !== 3) ||
    parts[0]?.length === 0 ||
    parts.at(-1)?.length === 0
  ) {
    return null;
  }

  try {
    const [encodedGridCode, encodedPresetInstanceId, encodedBulkOperationId] =
      parts.length === 2 ? [parts[0], '', parts[1]] : parts;

    const bulkOperationId = decodeURIComponent(encodedBulkOperationId);
    if (!BULK_OPERATION_ID_PATTERN.test(bulkOperationId)) {
      return null;
    }

    return {
      gridCode: decodeURIComponent(encodedGridCode),
      presetInstanceId:
        encodedPresetInstanceId.length === 0 ? null : decodeURIComponent(encodedPresetInstanceId),
      bulkOperationId,
    };
  } catch {
    return null;
  }
}

export function isBulkOperationLinkOwner(
  link: BulkOperationLink | null,
  gridCode: string,
  presetInstanceId: string | undefined
): link is BulkOperationLink {
  return link?.gridCode === gridCode && link.presetInstanceId === (presetInstanceId ?? null);
}
